import Control from "ol/control/Control";
import Feature from "ol/Feature";
import Draw, { createBox } from "ol/interaction/Draw";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import WKT from "ol/format/WKT";
import LineString from "ol/geom/LineString";
import Polygon from "ol/geom/Polygon";
import MultiPoint from "ol/geom/MultiPoint";
import { fromLonLat, toLonLat } from "ol/proj";
import { Style, Stroke, Fill, Circle as CircleStyle } from "ol/style";
import { DRAW_ICONS } from "./drawIcons";
import {
  mortonCodeFromCoordinate,
  mortonRingFromCode,
  mortonWktFromCode,
} from "../util/mortonTile";

// Shape tools. `type` is the ol/interaction/Draw geometry type; `multiVertex`
// flags the click-per-vertex tools (line/polygon) that get the
// Finish / Undo / Cancel action row and Enter/Backspace finishing.
const SHAPE_TOOLS = [
  { id: "point", type: "Point", icon: "point", title: "Draw a point" },
  {
    id: "line",
    type: "LineString",
    icon: "line",
    title: "Draw a line",
    multiVertex: true,
  },
  {
    id: "polygon",
    type: "Polygon",
    icon: "polygon",
    title: "Draw a polygon",
    multiVertex: true,
  },
  {
    id: "rectangle",
    type: "Circle",
    icon: "rectangle",
    title: "Draw a rectangle",
    box: true,
  },
];

// Tile tools place a single point; on finish the clicked coordinate is turned
// into a tile at the chosen level/resolution. `tile` selects the tile kind.
const TILE_TOOLS = [
  {
    id: "morton",
    type: "Point",
    icon: "morton",
    title: "Place a Morton tile",
    tile: "morton",
    levelLabel: "Level",
    levelMin: 0,
    levelMax: 23,
    levelDefault: 10,
  },
];

const ALL_TOOLS = [...SHAPE_TOOLS, ...TILE_TOOLS];

// Initial level-input range/value (Morton); adapts per tile tool on selection.
const DEFAULT_LEVEL = 10;
const DEFAULT_LEVEL_MAX = 23;

// Sketch styling. Committed edges are solid (with a light fill for polygons);
// only the segment to the cursor is dashed — the rubber-band look from wktmap.
// No fill is rasterised over the moving cursor sweep (only the committed area),
// which keeps per-pointermove rendering cheap.
const SKETCH_COLOR = "#3a3a3a";
const SOLID_STROKE = new Stroke({ color: SKETCH_COLOR, width: 1.5 });
const DASHED_STROKE = new Stroke({
  color: SKETCH_COLOR,
  width: 1.5,
  lineDash: [5, 5],
});
const COMMITTED_FILL = new Fill({ color: "rgba(58, 58, 58, 0.1)" });
const VERTEX_IMAGE = new CircleStyle({
  radius: 3,
  fill: new Fill({ color: SKETCH_COLOR }),
});
const VERTEX_STYLE = new Style({ image: VERTEX_IMAGE });
const DASHED_STYLE = new Style({ stroke: DASHED_STROKE });
const BOX_STYLE = [
  new Style({ fill: COMMITTED_FILL }),
  new Style({ stroke: DASHED_STROKE }),
];
const TILE_PREVIEW_STYLE = new Style({
  fill: new Fill({ color: "rgba(58, 58, 58, 0.12)" }),
  stroke: new Stroke({
    color: "rgba(58, 58, 58, 0.9)",
    width: 2,
    lineDash: [6, 4],
  }),
});

const START_HINT = {
  point: "Click on the map to place a point.",
  line: "Click to start drawing a line.",
  polygon: "Click to start drawing a shape.",
  rectangle: "Click and drag to draw a rectangle.",
  morton: "Click on the map to place a Morton tile.",
};
const CONTINUE_HINT = {
  line: "Click to add points; double-click or Enter to finish.",
  polygon: "Click to add points; click the first point or Enter to finish.",
};

// Round WKT coordinates to 7 decimals (~1 cm) so reprojection float noise
// (e.g. 45.43403509999999) doesn't end up in the box.
function roundWkt(wkt) {
  return wkt.replace(/-?\d+\.\d+/g, (n) =>
    String(parseFloat(parseFloat(n).toFixed(7))),
  );
}

/**
 * Collapsible sidebar toolbar for drawing geometries and placing tiles. On
 * finish, the result is written into the WKT input box, overwriting it; the
 * existing WKT layer renders it. The toolbar is placed in the DOM by
 * addDrawControl (it is not registered via map.addControl).
 */
export class DrawControl extends Control {
  constructor(opt_options) {
    const options = opt_options || {};
    // A <details> so the (rarely-used) toolbar collapses out of the way and
    // inherits the same card styling as the layer-category sections.
    const element = document.createElement("details");
    element.className = "draw-toolbar ol-unselectable";
    super({ element });

    this.olMap_ = options.map;
    this.wktControl_ = options.wktInputControl;
    this.wktFormat_ = new WKT();
    // Detached source: the in-progress sketch is rendered by Draw's own overlay;
    // the finished feature lands here, gets serialised, then the source is cleared.
    this.sketchSource_ = new VectorSource();
    this.tilePreviewSource_ = new VectorSource();
    this.tilePreviewLayer_ = new VectorLayer({
      source: this.tilePreviewSource_,
      style: TILE_PREVIEW_STYLE,
    });
    this.tilePreviewLayer_.setZIndex(10000);

    this.activeTool_ = null; // the active ALL_TOOLS entry, or null
    this.draw_ = null; // active ol/interaction/Draw, or null
    this.drawing_ = false; // true between drawstart and drawend/drawabort
    this.toolButtons_ = {}; // tool id -> button element
    this.tileOutput_ = "code"; // "code" | "wkt" — what a tile click writes
    this.tilePreviewActive_ = false;
    this.tilePreviewFeature_ = null;
    this.tilePreviewCode_ = null;
    this.tilePreviewFrame_ = null;
    this.pendingPreviewCoordinate_ = null;
    this.lastPreviewCoordinate_ = null;
    this.onPointerMove_ = this.handlePointerMove_.bind(this);
    this.onPointerLeave_ = this.handlePointerLeave_.bind(this);
    this.onKeyDown_ = this.handleKeyDown_.bind(this);

    this.buildDom_(element);
  }

  buildDom_(root) {
    const summary = document.createElement("summary");
    summary.textContent = "Draw";
    root.appendChild(summary);

    const tools = document.createElement("div");
    tools.className = "draw-toolbar-row draw-tools";
    tools.setAttribute("role", "toolbar");
    tools.setAttribute("aria-label", "Drawing tools");
    SHAPE_TOOLS.forEach((tool) =>
      tools.appendChild(this.makeToolButton_(tool)),
    );
    root.appendChild(tools);

    this.buildTilesGroup_(root);

    const actions = document.createElement("div");
    actions.className = "draw-actions";
    actions.setAttribute("role", "toolbar");
    actions.setAttribute("aria-label", "Drawing actions");
    actions.hidden = true;
    this.finishBtn_ = this.actionButton_(
      "finish",
      "Finish",
      "Finish drawing (Enter)",
      () => this.finish_(),
    );
    this.undoBtn_ = this.actionButton_(
      "undo",
      "Undo",
      "Delete last point (Backspace)",
      () => this.undo_(),
    );
    this.cancelBtn_ = this.actionButton_(
      "cancel",
      "Cancel",
      "Cancel drawing (Esc)",
      () => this.cancel_(),
    );
    actions.appendChild(this.finishBtn_);
    actions.appendChild(this.undoBtn_);
    actions.appendChild(this.cancelBtn_);
    this.actionsRow_ = actions;
    root.appendChild(actions);

    const status = document.createElement("p");
    status.className = "draw-status";
    status.setAttribute("aria-live", "polite");
    status.hidden = true;
    this.statusEl_ = status;
    root.appendChild(status);
  }

  buildTilesGroup_(root) {
    const tilesLabel = document.createElement("span");
    tilesLabel.className = "draw-tiles-label";
    tilesLabel.textContent = "Tiles";
    root.appendChild(tilesLabel);

    const tilesRow = document.createElement("div");
    tilesRow.className = "draw-toolbar-row draw-tiles";
    tilesRow.setAttribute("role", "toolbar");
    tilesRow.setAttribute("aria-label", "Tile tools");
    TILE_TOOLS.forEach((tool) =>
      tilesRow.appendChild(this.makeToolButton_(tool)),
    );

    const levelLabel = document.createElement("label");
    levelLabel.className = "draw-level-label";
    const levelText = document.createElement("span");
    levelText.textContent = "Level";
    levelLabel.appendChild(levelText);
    this.levelText_ = levelText;
    const levelInput = document.createElement("input");
    levelInput.type = "number";
    levelInput.className = "draw-level";
    levelInput.min = "0";
    levelInput.max = String(DEFAULT_LEVEL_MAX);
    levelInput.value = String(DEFAULT_LEVEL);
    levelInput.title = `Tile level / resolution`;
    levelInput.addEventListener("input", () => this.refreshTilePreview_());
    levelLabel.appendChild(levelInput);
    this.levelInput_ = levelInput;
    tilesRow.appendChild(levelLabel);
    root.appendChild(tilesRow);

    // Output-format toggle (Code | WKT), shared by all tile tools. The WKT box
    // decodes canonical tile codes; this only controls what a tile click writes.
    const outputRow = document.createElement("div");
    outputRow.className = "draw-toolbar-row draw-output";
    outputRow.setAttribute("role", "group");
    outputRow.setAttribute("aria-label", "Tile output format");
    const outLabel = document.createElement("span");
    outLabel.className = "draw-output-label";
    outLabel.textContent = "Tile output:";
    outputRow.appendChild(outLabel);
    this.outputButtons_ = {};
    [
      ["code", "Code"],
      ["wkt", "WKT"],
    ].forEach(([value, text]) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "caj-nav-btn draw-output-btn";
      btn.textContent = text;
      btn.title =
        value === "code"
          ? "Tile click writes the tile code"
          : "Tile click writes a WKT polygon";
      btn.setAttribute("aria-pressed", String(value === this.tileOutput_));
      btn.addEventListener("click", () => this.setTileOutput_(value));
      this.outputButtons_[value] = btn;
      outputRow.appendChild(btn);
    });
    root.appendChild(outputRow);
  }

  makeToolButton_(tool) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "caj-nav-btn draw-tool-btn";
    btn.title = tool.title;
    btn.setAttribute("aria-label", tool.title);
    btn.setAttribute("aria-pressed", "false");
    btn.innerHTML = DRAW_ICONS[tool.icon];
    btn.addEventListener("click", () => this.selectTool_(tool.id));
    this.toolButtons_[tool.id] = btn;
    return btn;
  }

  actionButton_(icon, text, title, onClick) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "caj-nav-btn draw-action-btn";
    btn.title = title;
    btn.setAttribute("aria-label", title);
    btn.innerHTML = DRAW_ICONS[icon] + `<span>${text}</span>`;
    btn.addEventListener("click", onClick);
    return btn;
  }

  setTileOutput_(value) {
    this.tileOutput_ = value;
    Object.keys(this.outputButtons_).forEach((key) => {
      this.outputButtons_[key].setAttribute(
        "aria-pressed",
        String(key === value),
      );
    });
  }

  // Point the shared level input at the active tile tool's range/label, clamping
  // the current value into the new range (or resetting to the tool's default).
  configureLevelInput_(tool) {
    this.levelInput_.min = String(tool.levelMin);
    this.levelInput_.max = String(tool.levelMax);
    this.levelInput_.title = `${tool.levelLabel} (${tool.levelMin}–${tool.levelMax})`;
    this.levelText_.textContent = tool.levelLabel;
    const cur = parseInt(this.levelInput_.value, 10);
    if (Number.isNaN(cur) || cur < tool.levelMin || cur > tool.levelMax) {
      this.levelInput_.value = String(tool.levelDefault);
    }
  }

  tileLevel_() {
    const tool = this.activeTool_;
    const min = tool ? tool.levelMin : 0;
    const max = tool ? tool.levelMax : DEFAULT_LEVEL_MAX;
    const def = tool ? tool.levelDefault : DEFAULT_LEVEL;
    const v = parseInt(this.levelInput_.value, 10);
    if (Number.isNaN(v)) return def;
    return Math.max(min, Math.min(max, v));
  }

  // OL Draw sketch styler. Splits the in-progress geometry into committed
  // (solid edges + light fill) and the moving segment to the cursor (dashed).
  makeSketchStyle_(tool) {
    if (tool.id === "polygon") {
      return (feature) => {
        const geom = feature.getGeometry();
        const t = geom.getType();
        if (t === "Polygon")
          return this.committedMovingStyles_(geom.getCoordinates()[0], true);
        if (t === "Point") return VERTEX_STYLE;
        return []; // skip the duplicate sketch-line; the Polygon branch covers it
      };
    }
    if (tool.id === "line") {
      return (feature) => {
        const geom = feature.getGeometry();
        const t = geom.getType();
        if (t === "LineString")
          return this.committedMovingStyles_(geom.getCoordinates(), false);
        if (t === "Point") return VERTEX_STYLE;
        return [];
      };
    }
    // point / tiles (Point) and rectangle (Polygon, click-drag)
    return (feature) => {
      const t = feature.getGeometry().getType();
      if (t === "Polygon") return BOX_STYLE;
      if (t === "LineString") return DASHED_STYLE;
      return VERTEX_STYLE;
    };
  }

  committedMovingStyles_(coords, isRing) {
    let r = coords;
    // A polygon ring is closed (last === first) while drawing; drop the dup so
    // the real cursor vertex is the last element.
    if (isRing && r.length > 1) {
      const a = r[0];
      const b = r[r.length - 1];
      if (a[0] === b[0] && a[1] === b[1]) r = r.slice(0, -1);
    }
    const n = r.length;
    if (n === 0) return [];
    const cursor = r[n - 1];
    const committed = r.slice(0, n - 1);
    const styles = [];
    if (isRing && committed.length >= 3) {
      styles.push(
        new Style({
          geometry: new Polygon([[...committed, committed[0]]]),
          fill: COMMITTED_FILL,
        }),
      );
    }
    if (committed.length >= 2) {
      styles.push(
        new Style({
          geometry: new LineString(committed),
          stroke: SOLID_STROKE,
        }),
      );
    }
    if (committed.length >= 1) {
      styles.push(
        new Style({
          geometry: new LineString([committed[committed.length - 1], cursor]),
          stroke: DASHED_STROKE,
        }),
      );
      if (isRing && committed.length >= 2) {
        styles.push(
          new Style({
            geometry: new LineString([cursor, committed[0]]),
            stroke: DASHED_STROKE,
          }),
        );
      }
      styles.push(
        new Style({ geometry: new MultiPoint(committed), image: VERTEX_IMAGE }),
      );
    }
    return styles;
  }

  selectTool_(id) {
    // Clicking the active tool again toggles it off.
    if (this.activeTool_ && this.activeTool_.id === id) {
      this.deactivate_();
      return;
    }
    this.deactivate_();
    const tool = ALL_TOOLS.find((t) => t.id === id);
    if (!tool) return;

    this.activeTool_ = tool;
    this.toolButtons_[id].setAttribute("aria-pressed", "true");
    if (tool.tile) this.configureLevelInput_(tool);

    this.draw_ = new Draw({
      source: this.sketchSource_,
      type: tool.type,
      geometryFunction: tool.box ? createBox() : undefined,
      style: this.makeSketchStyle_(tool),
    });
    this.draw_.on("drawstart", () => {
      this.drawing_ = true;
      if (tool.multiVertex) {
        this.actionsRow_.hidden = false;
        this.setStatus_(CONTINUE_HINT[tool.id]);
      }
    });
    this.draw_.on("drawend", (e) => this.handleDrawEnd_(e));
    this.draw_.on("drawabort", () => {
      this.drawing_ = false;
      this.actionsRow_.hidden = true;
      this.setStatus_(START_HINT[tool.id]);
    });

    this.olMap_.addInteraction(this.draw_);
    if (tool.tile) this.startTilePreview_();
    this.olMap_.getViewport().classList.add("draw-crosshair");
    document.addEventListener("keydown", this.onKeyDown_);
    this.setStatus_(START_HINT[tool.id]);
  }

  handleDrawEnd_(e) {
    const tool = this.activeTool_;
    const wantWkt = this.tileOutput_ === "wkt";
    let output;
    if (tool && tool.tile) {
      const [lon, lat] = toLonLat(e.feature.getGeometry().getCoordinates());
      const level = this.tileLevel_();
      const code = mortonCodeFromCoordinate(lon, lat, level);
      output = wantWkt ? mortonWktFromCode(code) : code;
    } else {
      output = roundWkt(
        this.wktFormat_.writeGeometry(e.feature.getGeometry(), {
          dataProjection: "EPSG:4326",
          featureProjection: "EPSG:3857",
        }),
      );
    }
    // Overwrite the box without moving the view (the user clicked where they look).
    this.wktControl_.set("zoomToExtent", false);
    this.wktControl_.set("wkt", output);
    // Tear down after the interaction's own event dispatch settles.
    window.setTimeout(() => this.deactivate_(), 0);
  }

  finish_() {
    if (this.draw_ && this.activeTool_ && this.activeTool_.multiVertex) {
      this.draw_.finishDrawing();
    }
  }

  undo_() {
    if (this.draw_) this.draw_.removeLastPoint();
  }

  cancel_() {
    if (this.draw_ && this.drawing_) {
      this.draw_.abortDrawing();
    } else {
      this.deactivate_();
    }
  }

  startTilePreview_() {
    if (this.tilePreviewActive_) return;
    this.tilePreviewActive_ = true;
    this.olMap_.addLayer(this.tilePreviewLayer_);
    this.olMap_.on("pointermove", this.onPointerMove_);
    this.olMap_
      .getViewport()
      .addEventListener("pointerleave", this.onPointerLeave_);
  }

  stopTilePreview_() {
    if (this.tilePreviewActive_) {
      this.tilePreviewActive_ = false;
      this.olMap_.un("pointermove", this.onPointerMove_);
      this.olMap_
        .getViewport()
        .removeEventListener("pointerleave", this.onPointerLeave_);
      this.olMap_.removeLayer(this.tilePreviewLayer_);
    }
    this.clearTilePreview_();
  }

  handlePointerMove_(e) {
    if (!this.activeTool_ || !this.activeTool_.tile || e.dragging) return;
    this.lastPreviewCoordinate_ = e.coordinate;
    this.scheduleTilePreview_(e.coordinate);
  }

  handlePointerLeave_() {
    this.lastPreviewCoordinate_ = null;
    this.clearTilePreview_();
  }

  refreshTilePreview_() {
    this.tilePreviewCode_ = null;
    if (this.tilePreviewActive_ && this.lastPreviewCoordinate_) {
      this.scheduleTilePreview_(this.lastPreviewCoordinate_);
    }
  }

  scheduleTilePreview_(coordinate) {
    this.pendingPreviewCoordinate_ = coordinate;
    if (this.tilePreviewFrame_ !== null) return;
    this.tilePreviewFrame_ = window.requestAnimationFrame(() => {
      this.tilePreviewFrame_ = null;
      const pending = this.pendingPreviewCoordinate_;
      this.pendingPreviewCoordinate_ = null;
      this.renderTilePreview_(pending);
    });
  }

  renderTilePreview_(coordinate) {
    if (!coordinate || !this.activeTool_ || !this.activeTool_.tile) {
      this.clearTilePreview_();
      return;
    }

    const [lon, lat] = toLonLat(coordinate);
    const code = mortonCodeFromCoordinate(lon, lat, this.tileLevel_());
    if (code === this.tilePreviewCode_ && this.tilePreviewFeature_) return;

    const ring = mortonRingFromCode(code).map(([ringLon, ringLat]) =>
      fromLonLat([ringLon, ringLat]),
    );
    const geometry = new Polygon([ring]);
    if (!this.tilePreviewFeature_) {
      this.tilePreviewFeature_ = new Feature({ geometry });
      this.tilePreviewSource_.addFeature(this.tilePreviewFeature_);
    } else {
      this.tilePreviewFeature_.setGeometry(geometry);
    }
    this.tilePreviewFeature_.set("mortonCode", code);
    this.tilePreviewCode_ = code;
  }

  clearTilePreview_() {
    if (this.tilePreviewFrame_ !== null) {
      window.cancelAnimationFrame(this.tilePreviewFrame_);
      this.tilePreviewFrame_ = null;
    }
    this.pendingPreviewCoordinate_ = null;
    this.tilePreviewCode_ = null;
    this.tilePreviewFeature_ = null;
    this.tilePreviewSource_.clear();
  }

  deactivate_() {
    this.stopTilePreview_();
    if (this.draw_) {
      if (this.drawing_) this.draw_.abortDrawing();
      this.olMap_.removeInteraction(this.draw_);
      this.draw_ = null;
    }
    this.sketchSource_.clear();
    this.drawing_ = false;
    if (this.activeTool_) {
      this.toolButtons_[this.activeTool_.id].setAttribute(
        "aria-pressed",
        "false",
      );
      this.activeTool_ = null;
    }
    this.actionsRow_.hidden = true;
    this.setStatus_(null);
    this.olMap_.getViewport().classList.remove("draw-crosshair");
    document.removeEventListener("keydown", this.onKeyDown_);
  }

  setStatus_(text) {
    if (!text) {
      this.statusEl_.hidden = true;
      this.statusEl_.textContent = "";
      return;
    }
    this.statusEl_.hidden = false;
    this.statusEl_.textContent = text;
  }

  handleKeyDown_(e) {
    // Don't hijack keys while the user is typing in a field (e.g. the WKT box).
    const tag = (e.target && e.target.tagName) || "";
    if (
      /^(input|textarea|select)$/i.test(tag) ||
      (e.target && e.target.isContentEditable)
    ) {
      return;
    }
    if (!this.draw_) return;
    if (e.key === "Enter") {
      this.finish_();
      e.preventDefault();
    } else if (
      e.key === "Backspace" ||
      ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z"))
    ) {
      this.undo_();
      e.preventDefault();
    } else if (e.key === "Escape") {
      this.cancel_();
      e.preventDefault();
    }
  }
}
